import { AxisBottom, AxisLeft } from '@visx/axis';
import {
    DataSourceGlyphCircle,
    TooltipDate,
    TooltipMetric,
    TooltipMetricLabel,
    TooltipMetricLabelWrapper,
    TooltipName,
} from '../PopularityTimeSeries/tooltip-components';
import {
    formatSeriesData,
    getPointerClickHandler,
    getPointerMoveHandler,
    yAxisTickFormat,
} from '../MultiPredictionTimeSeriesChart/utils';
import { scaleLinear, scaleTime } from 'd3-scale';
import styled, { useTheme } from 'styled-components';
import { useCallback, useMemo, useState } from 'react';
import { Chart } from 'components/DataVis/TimeSeriesChart';
import Drawer from 'components/Drawer';
import DriversOfGrowthDrawer from '../MultiPredictionTimeSeriesChart/components/DriversOfGrowthDrawer';
import DriversOfGrowthTooltip from '../MultiPredictionTimeSeriesChart/components/DriversOfGrowthTooltip';
import ErrorBoundary from 'containers/ErrorBoundary';
import { Group } from '@visx/group';
import { Line } from '@visx/shape';
import { ParentSize } from '@visx/responsive';
import { PlaceholderShimmerBackground } from 'utils/fragments';
import PredictionLineSeries from '../MultiPredictionTimeSeriesChart/components/PredictionLineSeries';
import Stack from '../../../Stack';
import TickLabel from '../MultiPredictionTimeSeriesChart/components/TickLabel';
import TooltipCard from '../MultiPredictionTimeSeriesChart/components/TooltipCard';
import dayjs from 'dayjs';
import { formatVolume } from 'utils/helpers';
import { getXAxisTickFns } from '../PopularityTimeSeries/utils';
import { useTooltip } from '@visx/tooltip';
import { voronoi } from '@visx/voronoi';
import { withMixpanel } from 'utils/app';

const X_AXIS_HEIGHT = 50;
const Y_AXIS_WIDTH = 70;
const STROKE_WIDTH = 3;

const formatAndParseTime = (v) => dayjs(v, 'YYYY-MM-DD').format('MMM YYYY');

const Placeholder = styled.div.attrs(({ shimmerWidth = '10000px' }) => ({
    shimmerWidth,
}))`
    grid-area: chart;
    min-height: ${({ height }) => `${height}px`};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius_sm};
    background: ${({ $showBackground, theme }) =>
        $showBackground ? theme.gray_5 : 'transparent'};
    ${({ isLoading }) => (isLoading ? PlaceholderShimmerBackground : null)}
`;

const GoogleTimeSeriesWrapper = styled.div`
    display: block;
`;

function MultiTimeSeriesChart({
    data,
    width,
    height,
    name,
    skipPrediction = false,
    country,
    category,
    categoryDepth,
    brand,
    dataSource = 'google',
    mixpanel,
}) {
    const theme = useTheme();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPointData, setSelectedPointData] = useState(null);
    const { xAxisTickFormat, xAxisTickValues } = getXAxisTickFns(data);

    const {
        showTooltip,
        hideTooltip,
        tooltipData,
        tooltipLeft,
        tooltipTop,
        tooltipOpen,
    } = useTooltip({
        tooltipOpen: false,
        tooltipLeft: 0,
        tooltipTop: 0,
        tooltipData: null,
    });

    const hasDriversOfGrowthContext = Boolean(country && (category || brand));
    const trackingMeta = {
        dataSource,
        country,
        ...(brand
            ? { brand, type: 'brand' }
            : { category, categoryDepth, type: 'category' }),
    };

    const handlePointClick = useCallback(
        (pointData) => {
            if (!hasDriversOfGrowthContext) return;
            mixpanel?.track('Click', {
                context: 'DRIVERS_OF_GROWTH_DRAWER_OPEN',
                ...trackingMeta,
            });
            setSelectedPointData(pointData);
            setDrawerOpen(true);
        },
        [hasDriversOfGrowthContext, mixpanel, trackingMeta],
    );

    const { accessors, formattedData, xMin, xMax, yMin, yMax } =
        formatSeriesData(data, { skipPrediction });
    const xScale = scaleTime()
        .domain([xMin, xMax])
        .range([Y_AXIS_WIDTH + STROKE_WIDTH, width - STROKE_WIDTH]);
    const yScale = scaleLinear()
        .domain([yMin, yMax])
        .range([
            height - X_AXIS_HEIGHT - STROKE_WIDTH,
            X_AXIS_HEIGHT + STROKE_WIDTH,
        ]);
    const x = (d) => xScale(dayjs(accessors.xAccessor(d)).toDate());
    const y = (d) => yScale(accessors.yAccessor(d));

    const dataVoronoi = useMemo(() => {
        const voronoiInstance = voronoi({ x, y, width, height });
        return data.map(({ timeSeries }) =>
            voronoiInstance(
                timeSeries.slice(1).filter((row) => {
                    const date = dayjs(accessors.xAccessor(row)).toDate();
                    return date >= xMin && date <= xMax;
                }),
            ),
        );
    }, [accessors, data, height, width, x, xMax, xMin, y]);

    return (
        <div css="position: relative;">
            <svg width={width} height={height}>
                <AxisLeft
                    left={Y_AXIS_WIDTH}
                    scale={yScale}
                    orientation="left"
                    tickFormat={yAxisTickFormat}
                    tickComponent={TickLabel}
                    numTicks={3}
                    hideZero={true}
                    hideAxisLine={true}
                    hideTicks={true}
                />
                <AxisBottom
                    orientation="bottom"
                    top={height - X_AXIS_HEIGHT}
                    scale={xScale}
                    tickValues={xAxisTickValues.range(xMin, xMax, 1)}
                    tickFormat={xAxisTickFormat}
                    tickComponent={TickLabel}
                    hideAxisLine={true}
                    hideTicks={true}
                />
                {formattedData.map(({ id, historical, prediction }) => (
                    <PredictionLineSeries
                        key={id}
                        x={x}
                        y={y}
                        historical={historical}
                        prediction={prediction}
                        strokeColor={theme.google_blue}
                        strokeColorPrediction={theme.purple_1}
                        predictionStrokeDasharray={true}
                    />
                ))}
                {tooltipOpen ? (
                    <Group>
                        <Line
                            from={{ x: tooltipData.x, y: 0 }}
                            to={{
                                x: tooltipData.x,
                                y: Math.max(height - X_AXIS_HEIGHT, 0),
                            }}
                            stroke="#3A3A3A"
                            strokeWidth={1}
                            strokeDasharray="2 2"
                        />
                        <DataSourceGlyphCircle
                            left={tooltipData.x}
                            top={tooltipData.y}
                            size={130}
                            $dataSource={
                                accessors.predictionAccessor(tooltipData?.datum)
                                    ? 'overview'
                                    : 'google'
                            }
                        />
                    </Group>
                ) : null}
                <rect
                    {...(hasDriversOfGrowthContext && {
                        'data-component': `${dataSource}-growth-drivers`,
                    })}
                    x={Y_AXIS_WIDTH}
                    y={0}
                    width={Math.max(width - Y_AXIS_WIDTH, 0)}
                    height={Math.max(height - X_AXIS_HEIGHT, 0)}
                    fill="transparent"
                    style={{
                        cursor: hasDriversOfGrowthContext
                            ? 'pointer'
                            : 'default',
                    }}
                    onPointerMove={getPointerMoveHandler({
                        data: formattedData,
                        dataVoronoi,
                        tooltipData,
                        showTooltip,
                        x,
                        y,
                    })}
                    onPointerOut={() => hideTooltip()}
                    onPointerUp={() => hideTooltip()}
                    onClick={
                        hasDriversOfGrowthContext
                            ? getPointerClickHandler({
                                  data: formattedData,
                                  dataVoronoi,
                                  x,
                                  y,
                                  onClick: handlePointClick,
                              })
                            : undefined
                    }
                />
            </svg>
            {hasDriversOfGrowthContext ? (
                <DriversOfGrowthTooltip
                    left={tooltipLeft}
                    top={tooltipTop}
                    chartWidth={width}
                    chartHeight={height}
                    accessors={accessors}
                    tooltipData={tooltipData}
                    tooltipOpen={tooltipOpen}
                    formatAndParseTime={formatAndParseTime}
                    metricLabel="searches"
                    sourceName="Google"
                    dataSource={dataSource}
                    country={country}
                    category={category}
                    brand={brand}
                />
            ) : tooltipOpen ? (
                <TooltipCard
                    left={tooltipLeft}
                    top={tooltipTop}
                    chartWidth={width}
                    chartHeight={height}
                    offset={40}
                    width={170}
                    height={150}
                >
                    <Stack $space="1rem">
                        <div>
                            <TooltipDate>
                                {tooltipData?.datum
                                    ? formatAndParseTime(
                                          accessors.xAccessor(
                                              tooltipData?.datum,
                                          ),
                                      )
                                    : null}
                            </TooltipDate>
                            <TooltipName>{name}</TooltipName>
                        </div>
                        <TooltipMetricLabelWrapper
                            $dataSource={
                                accessors.predictionAccessor(tooltipData?.datum)
                                    ? 'prediction'
                                    : 'google'
                            }
                        >
                            <TooltipMetric>
                                {formatVolume(
                                    accessors.yAccessor(tooltipData?.datum),
                                )}
                            </TooltipMetric>
                            <TooltipMetricLabel>Searches</TooltipMetricLabel>
                        </TooltipMetricLabelWrapper>
                    </Stack>
                </TooltipCard>
            ) : null}
            {hasDriversOfGrowthContext && (
                <Drawer
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    $width="42rem"
                >
                    <DriversOfGrowthDrawer
                        selectedPointData={selectedPointData}
                        setSelectedPointData={setSelectedPointData}
                        accessors={accessors}
                        country={country}
                        category={category}
                        categoryDepth={categoryDepth}
                        brand={brand}
                        dataSource={dataSource}
                        historicalData={formattedData[0]?.historical || []}
                    />
                </Drawer>
            )}
        </div>
    );
}

function MultiTimeSeriesChartOverview({
    children,
    data,
    chartHeight,
    isLoading,
    showPlaceholder,
    placeholderMessage,
    showPlaceholderBackground = true,
    Wrapper = GoogleTimeSeriesWrapper,
    country,
    category,
    categoryDepth,
    brand,
    dataSource = 'google',
    mixpanel,
    ...props
}) {
    return (
        <Wrapper>
            {children}
            {showPlaceholder ? (
                <Placeholder
                    height={chartHeight}
                    $showBackground={showPlaceholderBackground}
                    isLoading={isLoading}
                >
                    {placeholderMessage}
                </Placeholder>
            ) : (
                <div>
                    <Chart as="div" height={chartHeight}>
                        <ParentSize>
                            {({ width, height }) => (
                                <ErrorBoundary
                                    FallbackComponent={() => (
                                        <Placeholder
                                            $showBackground={true}
                                            height={chartHeight}
                                        >
                                            There was a problem showing this
                                            time series.
                                        </Placeholder>
                                    )}
                                >
                                    <MultiTimeSeriesChart
                                        data={data.filter(
                                            ({ timeSeries }) =>
                                                timeSeries &&
                                                timeSeries.length > 0,
                                        )}
                                        width={width}
                                        height={height}
                                        country={country}
                                        category={category}
                                        categoryDepth={categoryDepth}
                                        brand={brand}
                                        dataSource={dataSource}
                                        mixpanel={mixpanel}
                                        {...props}
                                    />
                                </ErrorBoundary>
                            )}
                        </ParentSize>
                    </Chart>
                </div>
            )}
        </Wrapper>
    );
}

export default withMixpanel(MultiTimeSeriesChartOverview);
